import { ItemImgList, ItemList, ItemToolTipList } from "@shared/types";
import { InventoryService } from "../services/InventoryService";
import { htmlArray } from "../helpers/webComponents";

/**
 * InventoryComponent represents an inventory UI component that extends HTMLElement.
 * This component is responsible for rendering and managing an inventory list with images and tooltips.
 */
export class InventoryComponent extends HTMLElement {
    private readonly _inventoryService: InventoryService = new InventoryService();
    private _inventoryElement: HTMLElement | null = null;
    private _elements: HTMLElement[] = htmlArray`
        <div class="menu">
            ${this.renderInventory()}
        </div>
        <div id="getTip" class="content"></div>
        
        <style>
            .inventory-item {
                width: 20%;
                height: 20%;
                margin: 5px;
                cursor: pointer;
            }
            .inventory-item:hover {
                transform: scale(1.1);
                transition: transform 0.2s;
                filter: brightness(1.2);
            }
            .inventory-item.key, 
            .inventory-item.doll, 
            .inventory-item.testament,
            .inventory-item.murderweapon,
            .inventory-item.Rake,
            .inventory-item.ring {
                border-radius: 10px;
                border: 10px solid black;
            }
            .sidebar > div {
                background-color: #780606;
                border-radius: 10px;
                border: 1px solid pink;
                padding: 10px;
            }
            .sidebar > div.menu {
                grid-area: menu;
                border-radius: 10px 10px 0 0;
                border-color: pink;
            }
            .content {
                grid-area: content;
                border-radius: 0 0 10px 10px;
                border-color: pink;
                background-color: #666666;
                size: 100%;
            }
        </style>
    `;

    /**
     * Called when the element is added to the document DOM.
     * Attaches a shadow DOM and triggers the initial rendering.
     */
    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });
        await this.render();
    }

    /**
     * Renders the inventory component.
     * Clears existing elements in the shadow DOM and appends the inventory structure.
     */
    private async render(): Promise<void> {
        if (!this.shadowRoot) return;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        await this.inventory();
        this.shadowRoot.append(...this._elements);
    }

    /**
     * Creates and returns an inventory container element.
     * @returns {HTMLElement} The inventory container element.
     */
    public renderInventory(): HTMLElement {
        const inventoryElement: HTMLElement = document.createElement("div");
        this._inventoryElement = inventoryElement;
        return inventoryElement;
    }

    /**
     * Fetches the inventory list, images, and tooltips from the InventoryService.
     * Dynamically creates inventory items and appends them to the inventory element.
     * @returns {Promise<void>} A promise that resolves when the inventory is rendered.
     */
    private async inventory(): Promise<void> {
        const inventoryList: ItemList = await this._inventoryService.inventoryExecute();
        const items: string[] = inventoryList.inventory;
        const inventoryImages: ItemImgList = await this._inventoryService.inventoryImageExecute();
        const inventoryTooltipList: ItemToolTipList = await this._inventoryService.inventoryTooltipExecute();

        if (!this.shadowRoot || !this._inventoryElement) return;

        const getTip: HTMLElement = this._elements[2];

        try {
            items.forEach((item: string) => {
                const itemElement: HTMLImageElement = document.createElement("img");
                const currentImage: string = inventoryImages[item as keyof ItemImgList];

                itemElement.src = currentImage;
                itemElement.className = `inventory-item ${item}`;
                itemElement.dataset.column = item;
                itemElement.id = item;

                // Event listener to display tooltip on click
                itemElement.addEventListener("click", () => {
                    const tip: string = inventoryTooltipList[item as keyof ItemToolTipList];
                    getTip.innerHTML = tip;
                });

                this._inventoryElement?.appendChild(itemElement);
            });
        }
        catch (error) {
            console.error("Error rendering inventory:", error);
        }
    }
}
